import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Tabs, Tab, Card, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FiSearch, FiZap, FiArrowLeft } from 'react-icons/fi';
import PageHeader from '../../components/PageHeader';
import UploadBillTab from './UploadBillTab';
import UploadedBillsTab from './UploadedBillsTab';
import BulkUploadTab from './BulkUploadTab';
import { clearParsedData } from './billParsingSlice';

const MODE_CARDS = [
  {
    key: 'manual',
    icon: FiSearch,
    iconBg: '#e7f3ff',
    iconColor: '#0d6efd',
    titleKey: 'Review & Save',
    descKey: 'Upload bills and review the parsed data. Make corrections if needed, then save manually.',
    buttonKey: 'Start',
    buttonVariant: 'outline-primary',
  },
  {
    key: 'auto',
    icon: FiZap,
    iconBg: '#fff3cd',
    iconColor: '#fd7e14',
    titleKey: 'Auto Process',
    descKey: 'Upload multiple bills at once. The system automatically parses and saves everything — no review needed.',
    buttonKey: 'Start',
    buttonVariant: 'outline-warning',
  },
];

const UploadModeLanding = ({ onSelect }) => {
  const { t } = useTranslation();
  return (
    <div style={{ maxWidth: '680px', margin: '32px auto 0' }}>
      <div className="text-center mb-4">
        <h6 className="text-muted mb-1" style={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '11px', fontWeight: 600 }}>
          {t('How would you like to process your bills?')}
        </h6>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {MODE_CARDS.map(({ key, icon: Icon, iconBg, iconColor, titleKey, descKey, buttonKey, buttonVariant }) => (
          <Card
            key={key}
            style={{ cursor: 'pointer', borderRadius: '10px', border: '1.5px solid #e9ecef', transition: 'box-shadow 0.15s, border-color 0.15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.09)'; e.currentTarget.style.borderColor = iconColor; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = '#e9ecef'; }}
            onClick={() => onSelect(key)}
          >
            <Card.Body style={{ padding: '24px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>
                <Icon size={22} style={{ color: iconColor }} />
              </div>
              <h6 style={{ fontWeight: 700, marginBottom: '8px', fontSize: '15px' }}>{t(titleKey)}</h6>
              <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '20px', lineHeight: 1.55 }}>{t(descKey)}</p>
              <Button variant={buttonVariant} size="sm" style={{ borderRadius: '6px', fontWeight: 500 }} onClick={(e) => { e.stopPropagation(); onSelect(key); }}>
                {t(buttonKey)} →
              </Button>
            </Card.Body>
          </Card>
        ))}
      </div>
    </div>
  );
};

const BillParsingHome = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('upload');
  const [uploadMode, setUploadMode] = useState(null);

  useEffect(() => {
    if (activeTab === 'upload') {
      setUploadMode(null);
      dispatch(clearParsedData());
    }
  }, [activeTab, dispatch]);

  const handleSelectMode = (mode) => {
    if (mode === 'manual') dispatch(clearParsedData());
    setUploadMode(mode);
  };

  const handleBack = () => {
    setUploadMode(null);
    dispatch(clearParsedData());
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <PageHeader title={t('Bill Parsing')} />
        </Col>
      </Row>
      <Row>
        <Col>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="custom-tab-bar">
            <Tab eventKey="upload" title={t('Upload')} className="custom-tab-content">
              <Row className="my-2">
                <Col>
                  {!uploadMode && <UploadModeLanding onSelect={handleSelectMode} />}
                  {uploadMode && (
                    <>
                      <button
                        className="btn btn-link p-0 mb-3"
                        style={{ fontSize: '13px', color: '#6c757d', textDecoration: 'none' }}
                        onClick={handleBack}
                      >
                        <FiArrowLeft size={14} className="mr-1" style={{ verticalAlign: 'middle' }} />
                        {t('Back')}
                      </button>
                      {uploadMode === 'manual' && <UploadBillTab onSuccess={() => setActiveTab('uploaded')} />}
                      {uploadMode === 'auto' && <BulkUploadTab />}
                    </>
                  )}
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="uploaded" title={t('Uploaded Bills')} className="custom-tab-content">
              <Row className="my-2">
                <Col>{activeTab === 'uploaded' && <UploadedBillsTab />}</Col>
              </Row>
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};

export default BillParsingHome;
